import React, { useState, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useGoogleLogin } from "@react-oauth/google";
import axios from "axios";
import { FiGlobe } from "react-icons/fi";
import LoadingPage from "../../components/LoadingPage/LoadingPage";
import { authAPI } from "../../services/api";
import { supportedLanguages } from "../../contexts/LanguageContext"; // 언어 리스트만 import
import { siteConfig, getPageTitle } from "../../config/siteConfig";
import mainLogo from "../../assets/main.png"; // 메인 로고 이미지 import
import googleLogo from "../../assets/Group.png"; // 구글 로고 이미지 import
import "./LoginPage.css";

/**
 * 다국어 지원을 위한 언어 코드 시스템
 *
 * 언어 코드는 로컬스토리지의 'i2m_language' 키에 저장됩니다.
 * 각 언어 코드는 다음과 같이 정의됩니다:
 * - ko: 한국어
 * - en: 영어
 * - ja: 일본어
 * - zh-CN: 중국어 간체
 * - zh-TW: 중국어 번체
 * - it: 이탈리아어
 * - fr: 프랑스어
 * - de: 독일어
 * - es: 스페인어
 * - ru: 러시아어
 * - pt: 포르투갈어
 * - ar: 아랍어
 * - hi: 힌디어
 *
 * 사용 방법:
 * 1. 현재 언어 코드 가져오기: localStorage.getItem('i2m_language')
 * 2. 언어 코드로 텍스트 가져오기: getText(languageCode, textKey)
 *
 * 이 시스템을 통해 애플리케이션 전체에서 일관된 다국어 지원을 제공할 수 있습니다.
 */

// 언어별 텍스트 (실제로는 더 많은 텍스트와 언어가 포함되어야 함)
// 언어별 텍스트
const translations = {
  ko: {
    title: "Electronic Data Capture",
    subtitle: "사용자 맞춤 eCRF 전문 커스텀 플랫폼",
    email: "이메일",
    password: "비밀번호",
    login: "로그인",
    or: "또는",
    googleLogin: "Google 계정으로 시작하기",
    registerPrompt: "일반 계정으로 가입하시겠습니까?",
    register: "회원가입",
    languageSelector: "언어 선택",
    chooseLanguage: "언어를 선택하세요!",
  },
  en: {
    title: "Electronic Data Capture",
    subtitle: "The Intelligent Platform for Customizable eCRF Solutions.",
    email: "Email",
    password: "Password",
    login: "Login",
    or: "OR",
    googleLogin: "Sign in with Google",
    registerPrompt: "Don't have an account?",
    register: "Register",
    languageSelector: "Select Language",
    chooseLanguage: "Choose Your Language!",
  },
  ja: {
    title: "Electronic Data Capture",
    subtitle: "カスタマイズ可能なeCRFソリューションのためのプラットフォーム。",
    email: "メールアドレス",
    password: "パスワード",
    login: "ログイン",
    or: "または",
    googleLogin: "Googleアカウントでログイン",
    registerPrompt: "アカウントをお持ちでないですか？",
    register: "登録",
    languageSelector: "言語選択",
    chooseLanguage: "言語を選択してください！",
  },
  "zh-CN": {
    title: "Electronic Data Capture",
    subtitle: "用户定制化 eCRF 解决方案的平台。",
    email: "电子邮箱",
    password: "密码",
    login: "登录",
    or: "或者",
    googleLogin: "使用Google账号登录",
    registerPrompt: "没有账号？",
    register: "注册",
    languageSelector: "选择语言",
    chooseLanguage: "请选择您的语言！",
  },
  "zh-TW": {
    title: "Electronic Data Capture",
    subtitle: "用戶定制化 eCRF 解決方案的平台。",
    email: "電子郵箱",
    password: "密碼",
    login: "登錄",
    or: "或者",
    googleLogin: "使用Google帳號登錄",
    registerPrompt: "沒有帳號？",
    register: "註冊",
    languageSelector: "選擇語言",
    chooseLanguage: "請選擇您的語言！",
  },
  it: {
    title: "Electronic Data Capture",
    subtitle: "La piattaforma per soluzioni eCRF personalizzabili.",
    email: "Email",
    password: "Password",
    login: "Accedi",
    or: "OPPURE",
    googleLogin: "Accedi con Google",
    registerPrompt: "Non hai un account?",
    register: "Registrati",
    languageSelector: "Seleziona lingua",
    chooseLanguage: "Scegli la tua lingua!",
  },
  fr: {
    title: "Electronic Data Capture",
    subtitle: "La plateforme pour des solutions eCRF personnalisables.",
    email: "Email",
    password: "Mot de passe",
    login: "Connexion",
    or: "OU",
    googleLogin: "Se connecter avec Google",
    registerPrompt: "Vous n'avez pas de compte ?",
    register: "S'inscrire",
    languageSelector: "Sélectionner la langue",
    chooseLanguage: "Choisissez votre langue !",
  },
  de: {
    title: "Electronic Data Capture",
    subtitle: "Die Plattform für anpassbare eCRF-Lösungen.",
    email: "E-Mail",
    password: "Passwort",
    login: "Anmelden",
    or: "ODER",
    googleLogin: "Mit Google anmelden",
    registerPrompt: "Haben Sie kein Konto?",
    register: "Registrieren",
    languageSelector: "Sprache auswählen",
    chooseLanguage: "Wählen Sie Ihre Sprache!",
  },
  es: {
    title: "Electronic Data Capture",
    subtitle: "La plataforma para soluciones eCRF personalizables.",
    email: "Correo electrónico",
    password: "Contraseña",
    login: "Iniciar sesión",
    or: "O",
    googleLogin: "Iniciar sesión con Google",
    registerPrompt: "¿No tiene cuenta?",
    register: "Registrarse",
    languageSelector: "Seleccionar idioma",
    chooseLanguage: "¡Elija su idioma!",
  },
  ru: {
    title: "Electronic Data Capture",
    subtitle: "Платформа для настраиваемых решений eCRF.",
    email: "Электронная почта",
    password: "Пароль",
    login: "Вход",
    or: "ИЛИ",
    googleLogin: "Войти через Google",
    registerPrompt: "Нет учетной записи?",
    register: "Регистрация",
    languageSelector: "Выбор языка",
    chooseLanguage: "Выберите ваш язык!",
  },
  pt: {
    title: "Electronic Data Capture",
    subtitle: "A plataforma para soluções eCRF personalizáveis.",
    email: "E-mail",
    password: "Senha",
    login: "Entrar",
    or: "OU",
    googleLogin: "Entrar com o Google",
    registerPrompt: "Não tem uma conta?",
    register: "Cadastrar",
    languageSelector: "Selecionar idioma",
    chooseLanguage: "Escolha seu idioma!",
  },
  ar: {
    title: "Electronic Data Capture",
    subtitle: "منصة لحلول eCRF القابلة للتخصيص.",
    email: "البريد الإلكتروني",
    password: "كلمة المرور",
    login: "تسجيل الدخول",
    or: "أو",
    googleLogin: "تسجيل الدخول باستخدام Google",
    registerPrompt: "ليس لديك حساب؟",
    register: "التسجيل",
    languageSelector: "اختيار اللغة",
    chooseLanguage: "اختر لغتك!",
  },
  hi: {
    title: "Electronic Data Capture",
    subtitle: "कस्टमाइज़ेबल eCRF समाधानों के लिए प्लेटफॉर्म।",
    email: "ईमेल",
    password: "पासवर्ड",
    login: "लॉग इन करें",
    or: "या",
    googleLogin: "Google के साथ साइन इन करें",
    registerPrompt: "खाता नहीं है?",
    register: "पंजीकरण करें",
    languageSelector: "भाषा चुनें",
    chooseLanguage: "अपनी भाषा चुनें!",
  },
};

// 번역 텍스트 가져오기 함수
const getText = (language, key) => {
  // 해당 언어의 번역이 있는 경우
  if (translations[language] && translations[language][key]) {
    return translations[language][key];
  }
  // 해당 언어 번역이 없으면 영어로 폴백
  if (translations["en"] && translations["en"][key]) {
    return translations["en"][key];
  }
  // 영어 번역도 없으면 키 그대로 반환
  return key;
};

// 에러 메시지 번역 함수 추가
const getErrorMessage = (language, error) => {
  const errorMessages = {
    ko: {
      "Invalid credentials": "아이디 또는 비밀번호가 잘못되었습니다.",
      "Login failed": "로그인에 실패했습니다.",
      "Google login failed": "Google 로그인에 실패했습니다. 다시 시도해주세요.",
      "Network error":
        "네트워크 오류가 발생했습니다. 인터넷 연결을 확인해주세요.",
      "Server error": "서버 오류가 발생했습니다. 잠시 후 다시 시도해주세요.",
      default: "로그인 중 오류가 발생했습니다. 다시 시도해주세요.",
    },
    en: {
      "Invalid credentials": "Invalid username or password.",
      "Login failed": "Login failed.",
      "Google login failed": "Google login failed. Please try again.",
      "Network error": "Network error. Please check your internet connection.",
      "Server error": "Server error. Please try again later.",
      default: "An error occurred during login. Please try again.",
    },
  };

  // 해당 언어의 번역이 있는지 확인
  const languageMessages = errorMessages[language] || errorMessages.en;

  // 특정 에러 메시지에 대한 번역이 있는지 확인
  return languageMessages[error] || languageMessages.default;
};

const LoginPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });

  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [language, setLanguage] = useState("ko"); // 기본 언어: 한국어

  // 컴포넌트 마운트 시 저장된 언어 설정 불러오기
  useEffect(() => {
    // 'i2m_language' 키에서 언어 코드를 불러옴 (ko, en, ja 등)
    const savedLanguage = localStorage.getItem("i2m_language");
    if (savedLanguage) {
      setLanguage(savedLanguage);
    }

    // 페이지 제목 설정
    document.title = getPageTitle("login", { language: savedLanguage || "ko" });
  }, []);

  // 언어 변경 핸들러
  const handleLanguageChange = (e) => {
    const newLanguage = e.target.value; // 언어 코드 (ko, en, ja 등)
    setLanguage(newLanguage);

    // 'i2m_language' 키에 언어 코드를 저장 (실제 언어 이름이 아닌 코드)
    localStorage.setItem("i2m_language", newLanguage);

    // 페이지 제목도 함께 업데이트
    document.title = getPageTitle("login", { language: newLanguage });

    // 언어 코드 저장 후 콘솔에 로그 출력 (개발용)
    console.log(`언어가 변경되었습니다. 언어 코드: ${newLanguage}`);
    // 실제 애플리케이션에서는 여기서 i18n 라이브러리를 사용하여 전역 언어 설정을 변경할 수 있음
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      // authAPI.login 함수 사용
      const loginResponse = await authAPI.login(formData);

      if (loginResponse.success && loginResponse.token) {
        localStorage.setItem("token", loginResponse.token);
        console.log("로그인 성공, 토큰 저장 완료");
        console.log("대시보드로 이동 시도 1: navigate 직접 호출");

        // 대안 1: 직접 navigate 호출
        navigate("/dashboard");

        // 대안 2: setTimeout + navigate
        setTimeout(() => {
          console.log("대시보드로 이동 시도 2: setTimeout + navigate");
          navigate("/dashboard");
        }, 100);

        // 대안 3: setTimeout + window.location.href (가장 확실한 방법)
        setTimeout(() => {
          console.log("대시보드로 이동 시도 3: window.location.href");
          window.location.href = "/dashboard";
        }, 200);
      } else {
        setError("Login failed");
      }
    } catch (err) {
      console.error("로그인 에러:", err);
      setError(err.response?.data?.message || "Server error");
    } finally {
      setIsLoading(false);
    }
  };

  const googleLogin = useGoogleLogin({
    onSuccess: async (response) => {
      setIsLoading(true);
      try {
        // Google API에서 사용자 정보 가져오기
        const userInfo = await axios.get(
          "https://www.googleapis.com/oauth2/v3/userinfo",
          {
            headers: {
              Authorization: `Bearer ${response.access_token}`,
            },
          }
        );

        // authAPI.googleLogin 함수 사용
        const loginResponse = await authAPI.googleLogin({
          credential: response.access_token,
          email: userInfo.data.email,
          name: userInfo.data.name,
          googleId: userInfo.data.sub,
        });

        // 로그인 처리
        if (loginResponse.success && loginResponse.token) {
          localStorage.setItem("token", loginResponse.token);
          console.log("구글 로그인 성공, 토큰 저장 완료");
          console.log("대시보드로 이동 시도 1: navigate 직접 호출");

          // 대안 1: 직접 navigate 호출
          navigate("/dashboard");

          // 대안 2: setTimeout + navigate
          setTimeout(() => {
            console.log("대시보드로 이동 시도 2: setTimeout + navigate");
            navigate("/dashboard");
          }, 100);

          // 대안 3: setTimeout + window.location.href (가장 확실한 방법)
          setTimeout(() => {
            console.log("대시보드로 이동 시도 3: window.location.href");
            window.location.href = "/dashboard";
          }, 200);
        } else {
          setError("Google login failed");
        }
      } catch (err) {
        console.error("Google 로그인 에러:", err);
        setError("Google login failed");
      } finally {
        setIsLoading(false);
      }
    },
  });

  // 이제 googleSignUp 사용 대신 googleLogin 사용
  const handleGoogleButtonClick = () => {
    googleLogin();
  };

  return (
    <>
      {isLoading && <LoadingPage />}
      <div className="login-container">
        {/* 언어 선택 드롭다운 */}
        <div className="language-selector-container">
          <h2 className="language-title">
            {getText(language, "chooseLanguage")}
          </h2>
          <div className="language-selector">
            <FiGlobe className="globe-icon" />
            <select value={language} onChange={handleLanguageChange}>
              {supportedLanguages.map((lang) => (
                <option key={lang.code} value={lang.code}>
                  {lang.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="login-box">
          <div className="logo-container">
            <img src={mainLogo} alt="Main Logo" className="main-logo" />
          </div>
          <p className="description">
            {getText(language, "subtitle") || siteConfig.siteDescription}
          </p>

          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <input
                type="email"
                name="email"
                placeholder={getText(language, "email")}
                value={formData.email}
                onChange={handleChange}
                required
              />
            </div>
            <div className="form-group">
              <input
                type="password"
                name="password"
                placeholder={getText(language, "password")}
                value={formData.password}
                onChange={handleChange}
                required
              />
            </div>
            {error && (
              <div className="error-message">
                {getErrorMessage(language, error)}
              </div>
            )}
            <button type="submit" className="login-button">
              {getText(language, "login")}
            </button>
          </form>

          <div className="divider">
            <span>{getText(language, "or")}</span>
          </div>

          <button className="google-button" onClick={handleGoogleButtonClick}>
            <img src={googleLogo} alt="Google" className="google-logo" />
            <span>{getText(language, "googleLogin")}</span>
          </button>

          <div className="register-link">
            {getText(language, "registerPrompt")}{" "}
            <NavLink to="/register">{getText(language, "register")}</NavLink>
          </div>
        </div>
      </div>
    </>
  );
};

export default LoginPage;
